import { NavLink, Link, useLocation } from 'react-router'
import { useRef, useEffect, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Building2,
  Users,
  UserCog,
  FileText,
  FileSignature,
  Receipt,
  Banknote,
  Settings,
  ChevronLeft,
  ChevronRight,
  Wrench,
  Package,
  BarChart3,
  Bell,
  Car,
  Wallet,
  Clock,
  ClipboardList,
  Hammer,
  Building,
  Landmark,
  BookOpen,
  BookText,
  TrendingUp,
  Archive,
  X,
  Megaphone,
  Scale,
  Droplets,
  Gift,
  Tag,
  Ticket,
  Coins,
  UserCheck,
  Phone,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useFeatureFlags } from '@/lib/featureFlags'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useWarrantyAlerts } from '@/hooks/useCmms'
import { useModuleEnabled } from '@/hooks/useModuleEnabled'
import { useSupportSession } from '@/hooks/useSupportSession'
import { BetaTag } from '@/components/ui/BetaTag'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'

export interface SidebarItem {
  labelKey: string
  icon: ReactNode
  href: string
  testId: string
  badge?: string | number
  end?: boolean
}

export interface SidebarProps {
  collapsed?: boolean
  onToggle?: () => void
  mobile?: boolean
  onClose?: () => void
  className?: string
}

const dashboardItem: SidebarItem = {
  labelKey: 'nav.dashboard',
  icon: <LayoutDashboard className="w-5 h-5" />,
  href: '/',
  testId: 'nav-dashboard',
  end: true,
}

const navItems: SidebarItem[] = [
  { labelKey: 'nav.properties', icon: <Building2 className="w-5 h-5" />, href: '/properties', testId: 'nav-properties' },
  { labelKey: 'nav.customers', icon: <Users className="w-5 h-5" />, href: '/customers', testId: 'nav-customers' },
  { labelKey: 'nav.leases', icon: <FileSignature className="w-5 h-5" />, href: '/leases', testId: 'nav-leases' },
  { labelKey: 'nav.meters', icon: <Droplets className="w-5 h-5" />, href: '/meter-readings', testId: 'nav-meters' },
  { labelKey: 'nav.invoices', icon: <Receipt className="w-5 h-5" />, href: '/invoices', testId: 'nav-invoices' },
  { labelKey: 'nav.payments', icon: <Banknote className="w-5 h-5" />, href: '/payments', testId: 'nav-payments' },
  { labelKey: 'nav.maintenance', icon: <Wrench className="w-5 h-5" />, href: '/maintenances', testId: 'nav-maintenance' },
  { labelKey: 'nav.parcels', icon: <Package className="w-5 h-5" />, href: '/parcels', testId: 'nav-parcels' },
  { labelKey: 'nav.visitor_arrivals', icon: <UserCheck className="w-5 h-5" />, href: '/visitor-arrivals', testId: 'nav-visitor-arrivals' },
  { labelKey: 'nav.parking', icon: <Car className="w-5 h-5" />, href: '/parking', testId: 'nav-parking' },
  { labelKey: 'nav.expenses', icon: <Wallet className="w-5 h-5" />, href: '/expenses', testId: 'nav-expenses' },
  { labelKey: 'nav.timesheet', icon: <Clock className="w-5 h-5" />, href: '/timesheet', testId: 'nav-timesheet' },
]

// Positional lookups into `navItems` silently broke twice when new entries
// were inserted mid-array (T133-021 visitor_arrivals, T133-023 staff_calendar),
// which dropped Expenses and Timesheet out of the sidebar entirely. Address
// items by href instead — a bad key throws at render rather than rendering the
// wrong link.
const navItem = (href: string): SidebarItem => {
  const found = navItems.find((i) => i.href === href)
  if (!found) throw new Error(`Sidebar: no navItem for href "${href}"`)
  return found
}

const cmmsNavItems: SidebarItem[] = [
  { labelKey: 'nav.equipment', icon: <Hammer className="w-5 h-5" />, href: '/equipment', testId: 'nav-equipment' },
  { labelKey: 'nav.work_orders', icon: <ClipboardList className="w-5 h-5" />, href: '/work-orders', testId: 'nav-work-orders' },
  { labelKey: 'nav.vendors', icon: <Building className="w-5 h-5" />, href: '/vendors', testId: 'nav-vendors' },
]

const financeNavItems: SidebarItem[] = [
  { labelKey: 'nav.bank_reconciliation', icon: <Landmark className="w-5 h-5" />, href: '/bank-reconciliation', testId: 'nav-bank-reconciliation' },
  // T129-012: Closing Package — visible to admin/owner AND accountant (D-5)
  { labelKey: 'nav.closing_package', icon: <Package className="w-5 h-5" />, href: '/closing-package', testId: 'nav-closing-package' },
]

const accountingNavItems: SidebarItem[] = [
  { labelKey: 'nav.chart_of_accounts', icon: <BookOpen className="w-5 h-5" />, href: '/accounting/chart-of-accounts', testId: 'nav-chart-of-accounts' },
  { labelKey: 'nav.journal_entries', icon: <BookText className="w-5 h-5" />, href: '/accounting/journal-entries', testId: 'nav-journal-entries' },
  { labelKey: 'nav.financial_statements', icon: <TrendingUp className="w-5 h-5" />, href: '/accounting/financial-statements', testId: 'nav-financial-statements' },
  { labelKey: 'nav.fixed_assets', icon: <Archive className="w-5 h-5" />, href: '/accounting/fixed-assets', testId: 'nav-fixed-assets' },
]

// EP-160: admin entries a FlowRent support session does not get.
const SUPPORT_SESSION_HIDDEN_NAV = ['/settings/team', '/settings/activity-log']

const adminNavItems: SidebarItem[] = [
  { labelKey: 'nav.reports', icon: <BarChart3 className="w-5 h-5" />, href: '/reports', testId: 'nav-reports' },
  { labelKey: 'nav.templates', icon: <FileText className="w-5 h-5" />, href: '/settings/templates', testId: 'nav-templates' },
  { labelKey: 'nav.team', icon: <UserCog className="w-5 h-5" />, href: '/settings/team', testId: 'nav-team' },
  { labelKey: 'nav.activity_log', icon: <Bell className="w-5 h-5" />, href: '/settings/activity-log', testId: 'nav-activity-log' },
  // Settings was last in this list — Aor (round 3) scanned past it because it
  // was buried at the bottom of the long admin section. Promoted to the
  // sidebar footer above Logout, where settings-style controls naturally belong.
]

export function Sidebar({ collapsed = false, onToggle, mobile = false, onClose, className = '' }: SidebarProps) {
  const { t } = useTranslation('common')
  const { user } = useAuth()
  const featureFlags = useFeatureFlags()
  const { data: accountSettings } = useAccountSettings()

  const isAdmin = user?.role === 'admin' || user?.role === 'owner'
  // T129-008: accountant sees money-set sidebar items only (D-5).
  const isAccountant = user?.role === 'accountant'
  // EP-58 T5 — condominium accounts have no per-unit meters; hide the Meters
  // nav entry entirely.
  const isCondominium = accountSettings?.account_type === 'condominium'
  // D9 (EP-147): these predicates now come from the shared useModuleEnabled
  // hook so the sidebar and the router's ModuleRoute guard can never drift
  // apart (both consume the exact same computation).
  const showCmms = useModuleEnabled('cmms')
  const showAccounting = useModuleEnabled('accounting')
  const showDebtCollection = useModuleEnabled('debtCollection')
  const showAnnouncements = useModuleEnabled('announcements')
  const showIncentivesRewards = useModuleEnabled('incentivesRewards')
  // No route needs the retention module standalone. Only this file's own
  // OR-derivation into showIncentivesAny does, so it stays a local flag-only
  // read rather than a module key of its own.
  const showIncentivesRetention =
    featureFlags.renewalBenefits && (accountSettings?.settings?.renewal_benefits_enabled ?? false)
  const showIncentivesAny = useModuleEnabled('incentivesAny')
  // EP-133 T133-025 — visitor management is dual-gated server-side by
  // VisitorManagementModuleGate (Unleash `visitor_management` AND the
  // per-account `visitor_management_enabled` setting). Both nav entries below
  // were rendered unconditionally, so every account saw Visitor Arrivals and
  // Staff Calendar while every request behind them returned 403 "Module not
  // enabled". Mirror the server's predicate exactly.
  const showVisitorManagement = useModuleEnabled('visitorManagement')
  // Only asked for when the count can show: with the module on and for a role
  // the policy lets in (the accountant is not; it 403ed on every page).
  const { data: warrantyAlertsData } = useWarrantyAlerts(showCmms && !isAccountant)
  const warrantyAlertCount = showCmms ? (warrantyAlertsData?.data?.length ?? 0) : 0
  // EP-160 S3: the team and the activity log are refused in a FlowRent
  // support session (SupportSession::Blocked), so they are not offered.
  const supportSession = useSupportSession().active

  // EP-111 T111-8 — CAF sidebar item, dual-gated on the condominium_enabled
  // setting AND the account having >=1 condominium-type property (canonical
  // accessor per docs/ep-111-condominium-property-type.md D4).
  const showCaf = useModuleEnabled('commonAreaFees')
  // EP-111 T111-9 — Co-owner sidebar item, gated on condominium_enabled ONLY
  // (unlike CAF, no >=1 condo property requirement — a co-owner can be added
  // as soon as the module is on, which is also how the operator marks their
  // first property condominium in the first place).
  const showSharedOwners = useModuleEnabled('condominium')

  const navRef = useRef<HTMLElement>(null)
  const location = useLocation()

  // Vehicle/parking sub-routes nest under /properties/:id/parking/* but
  // conceptually belong to the Parking module. When on those paths,
  // suppress the Properties highlight and force-light the Parking link
  // instead.
  const isParkingContext = (() => {
    const p = location.pathname
    if (p.startsWith('/parking')) return true
    if (/^\/properties\/[^/]+\/parking(\/|$)/.test(p)) return true
    return false
  })()

  const computeActive = (href: string, navLinkActive: boolean): boolean => {
    if (href === '/properties') return navLinkActive && !isParkingContext
    if (href === '/parking') return navLinkActive || isParkingContext
    return navLinkActive
  }

  // Scroll active nav item into view on route change
  useEffect(() => {
    const timer = setTimeout(() => {
      const activeEl = navRef.current?.querySelector('a.bg-primary')
      activeEl?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }, 100)
    return () => clearTimeout(timer)
  }, [location.pathname])

  const handleNavClick = () => {
    if (mobile && onClose) onClose()
  }

  return (
    <aside
      className={`flex flex-col h-dvh bg-base-100 transition-all duration-300 ${
        mobile ? 'w-full' : collapsed ? 'w-16 border-r border-base-300' : 'w-64 border-r border-base-300'
      } ${className}`}
    >
      {/* Logo */}
      <div className="flex items-center min-h-16 px-4 pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-5 border-b border-base-300">
        <Link to="/" onClick={handleNavClick} className="flex items-center flex-1 min-w-0 hover:opacity-80 transition-opacity">
          {!collapsed && (
            // min-w-0 + shrink-0 icon + truncate wordmark: B-022 — never let the
            // row exceed the panel width (which clipped 'FlowRent' to 'owRent').
            <div className="flex items-center gap-2 min-w-0">
              <img src="/icon-clean.svg" alt="" className="w-8 h-8 rounded-lg ring-1 ring-base-300 shrink-0" />
              <span className="font-bold text-lg truncate">FlowRent</span>
              <BetaTag />
            </div>
          )}
          {collapsed && (
            <img src="/icon-clean.svg" alt="" className="w-8 h-8 rounded-lg ring-1 ring-base-300 mx-auto" />
          )}
        </Link>
        {mobile && onClose && (
          <Button
            variant="ghost"
            size="sm"
            shape="circle"
            onClick={onClose}
            aria-label={t('labels.close_menu')}
          >
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav ref={navRef} className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {/* Dashboard — top-level landing page */}
          <li key={dashboardItem.href}>
            <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t(dashboardItem.labelKey) : undefined}>
              <NavLink
                to={dashboardItem.href}
                end={dashboardItem.end}
                onClick={handleNavClick}
                aria-label={t(dashboardItem.labelKey)}
                data-testid={dashboardItem.testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-content'
                      : 'hover:bg-base-200 text-base-content'
                  } ${collapsed ? 'justify-center' : ''}`
                }
              >
                {dashboardItem.icon}
                {!collapsed && <span className="flex-1">{t(dashboardItem.labelKey)}</span>}
              </NavLink>
            </div>
          </li>

          {/* Core items: Properties, Customers, [Co-owner], Leases (no section
              header). Hidden for accountant — these are not in the D-5 money set.
              The Co-owner item (EP-111 T111-9) is a STANDALONE gated
              block inserted BETWEEN Customers and Leases (per the D9/D11
              mockup) — so this renders Properties + Customers, then
              Co-owner, then Leases via a shared renderer instead of one
              flat .map over the first three items. */}
          {!isAccountant && (() => {
            const renderCoreItem = (item: SidebarItem) => {
              // EP-59 V3 — on condominium accounts, the Leases nav label
              // reads "Co-Ownerships" (route still /leases). All other
              // labels pass through the regular i18n key unchanged.
              const labelKey =
                item.testId === 'nav-leases' && isCondominium
                  ? 'nav.co_ownerships'
                  : item.labelKey
              return (
                <li key={item.href}>
                  <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t(labelKey) : undefined}>
                    <NavLink
                      to={item.href}
                      end={item.end}
                      onClick={handleNavClick}
                      aria-label={t(labelKey)}
                      data-testid={item.testId}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                          computeActive(item.href, isActive)
                            ? 'bg-primary text-primary-content'
                            : 'hover:bg-base-200 text-base-content'
                        } ${collapsed ? 'justify-center' : ''}`
                      }
                    >
                      {item.icon}
                      {!collapsed && (
                        <>
                          <span className="flex-1">{t(labelKey)}</span>
                          {item.badge && (
                            <Badge variant="count" size="sm">{item.badge}</Badge>
                          )}
                        </>
                      )}
                    </NavLink>
                  </div>
                </li>
              )
            }

            return (
              <>
                {[navItem('/properties'), navItem('/customers')].map(renderCoreItem)}

                {/* Co-owner (EP-111 T111-9, standalone gated block directly
                    below Customers — mirrors the showAnnouncements pattern
                    rather than being spliced into navItems). Gate:
                    condominium_enabled only (no property-count requirement,
                    unlike CAF — a co-owner can be added as soon as the module
                    is on). */}
                {showSharedOwners && (
                  <li key="/co-owners">
                    <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t('nav.shared_owners') : undefined}>
                      <NavLink
                        to="/co-owners"
                        onClick={handleNavClick}
                        aria-label={t('nav.shared_owners')}
                        data-testid="nav-co-owners"
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                            isActive
                              ? 'bg-primary text-primary-content'
                              : 'hover:bg-base-200 text-base-content'
                          } ${collapsed ? 'justify-center' : ''}`
                        }
                      >
                        <UserCheck className="w-5 h-5" />
                        {!collapsed && <span className="flex-1">{t('nav.shared_owners')}</span>}
                      </NavLink>
                    </div>
                  </li>
                )}

                {/* EP-135 Tier 0 — contact directory (same gate as the CRUD list) */}
                {showSharedOwners && !collapsed && (
                  <li key="/co-owners/directory" className="ml-8">
                    <NavLink
                      to="/co-owners/directory"
                      onClick={handleNavClick}
                      aria-label={t('nav.shared_owner_directory')}
                      data-testid="nav-co-owner-directory"
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          isActive
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-base-200 text-base-content/70'
                        }`
                      }
                    >
                      <Phone className="w-4 h-4" />
                      <span className="flex-1">{t('nav.shared_owner_directory')}</span>
                    </NavLink>
                  </li>
                )}

                {/* EP-135 Tier 1 — Governance (Meetings + Committee). Gated on
                    condominium_enabled ONLY (no property-count requirement —
                    meetings can be planned ahead of a sale). Hidden for
                    accountant (governance surface, not money per D-13). */}
                {showSharedOwners && !isAccountant && (
                  <>
                    <li key="/meetings">
                      <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t('nav.meetings') : undefined}>
                        <NavLink
                          to="/meetings"
                          onClick={handleNavClick}
                          aria-label={t('nav.meetings')}
                          data-testid="nav-meetings"
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                              isActive
                                ? 'bg-primary text-primary-content'
                                : 'hover:bg-base-200 text-base-content'
                            } ${collapsed ? 'justify-center' : ''}`
                          }
                        >
                          <ClipboardList className="w-5 h-5" />
                          {!collapsed && <span className="flex-1">{t('nav.meetings')}</span>}
                        </NavLink>
                      </div>
                    </li>
                    {!collapsed && (
                      <li key="/committee" className="ml-8">
                        <NavLink
                          to="/committee"
                          onClick={handleNavClick}
                          aria-label={t('nav.committee')}
                          data-testid="nav-committee"
                          className={({ isActive }) =>
                            `flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                              isActive
                                ? 'bg-primary/10 text-primary'
                                : 'hover:bg-base-200 text-base-content/70'
                            }`
                          }
                        >
                          <Users className="w-4 h-4" />
                          <span className="flex-1">{t('nav.committee')}</span>
                        </NavLink>
                      </li>
                    )}
                  </>
                )}

                {renderCoreItem(navItem('/leases'))}
              </>
            )
          })()}

          {/* Finance section header: Meters, Invoices, Payments, Expenses, and
              — for admin/owner/accountant — Bank Reconciliation + Closing
              Package. Those last two used to render under a SECOND "Finance"
              header further down, so the sidebar showed the same heading twice;
              they are money surfaces and belong in this one group.
              Parking was previously listed here by mistake (the section
              comment said Operations but the array slice pulled index 8 =
              parking). It now lives under Operations where it belongs.
              Accountant skips meters (not in D-5 money set) but shares this
              header. */}
          {!collapsed && (
            <li className="pt-3 pb-1">
              <span className="px-3 text-xs font-semibold text-base-content/40 uppercase tracking-wider">
                {t('nav.finance')}
              </span>
            </li>
          )}
          {collapsed && <li className="border-t border-base-200 my-2" />}
          {!isAccountant && [
            ...(isCondominium ? [] : [navItem('/meter-readings')]),
          ].map((item) => (
            <li key={item.href}>
              <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t(item.labelKey) : undefined}>
                <NavLink
                  to={item.href}
                  end={item.end}
                  onClick={handleNavClick}
                  aria-label={t(item.labelKey)}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-content'
                        : 'hover:bg-base-200 text-base-content'
                    } ${collapsed ? 'justify-center' : ''}`
                  }
                >
                  {item.icon}
                  {!collapsed && (
                    <>
                      <span className="flex-1">{t(item.labelKey)}</span>
                      {item.badge && (
                        <Badge variant="count" size="sm">{item.badge}</Badge>
                      )}
                    </>
                  )}
                </NavLink>
              </div>
            </li>
          ))}

          {/* CAF (EP-111 T111-8, standalone gated block directly below Meters —
              mirrors the showAnnouncements pattern below rather than being
              spliced into navItems, which other sections index into by
              position). Dual-gate: condominium_enabled setting AND the
              account has >=1 condominium-type property.
              Accountant excluded (not in D-5 money set). */}
          {showCaf && !isAccountant && (
            <li key="/common-area-fees">
              <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t('nav.caf') : undefined}>
                <NavLink
                  to="/common-area-fees"
                  onClick={handleNavClick}
                  aria-label={t('nav.caf')}
                  data-testid="nav-caf"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-content'
                        : 'hover:bg-base-200 text-base-content'
                    } ${collapsed ? 'justify-center' : ''}`
                  }
                >
                  <Coins className="w-5 h-5" />
                  {!collapsed && <span className="flex-1">{t('nav.caf')}</span>}
                </NavLink>
              </div>
            </li>
          )}

          {[
            navItem('/invoices'),
            navItem('/payments'),
            navItem('/expenses'),
            // Bank Reconciliation + Closing Package (T129-012): money surfaces,
            // so they sit in this Finance group rather than under a duplicate
            // Finance header of their own. Admin/owner + accountant only.
            ...(isAdmin || isAccountant ? financeNavItems : []),
          ].map((item) => (
            <li key={item.href}>
              <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t(item.labelKey) : undefined}>
                <NavLink
                  to={item.href}
                  end={item.end}
                  onClick={handleNavClick}
                  aria-label={t(item.labelKey)}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-content'
                        : 'hover:bg-base-200 text-base-content'
                    } ${collapsed ? 'justify-center' : ''}`
                  }
                >
                  {item.icon}
                  {!collapsed && (
                    <>
                      <span className="flex-1">{t(item.labelKey)}</span>
                      {item.badge && (
                        <Badge variant="count" size="sm">{item.badge}</Badge>
                      )}
                    </>
                  )}
                </NavLink>
              </div>
            </li>
          ))}

          {/* Operations section header: Maintenance, Parcels, Parking, Timesheet, Announcements.
              Accountant (D-5) sees only Timesheet from this section; the other items
              are scoped to non-accountant roles. The section header itself is suppressed
              when the accountant would see no items (only timesheet is shown separately). */}
          {!isAccountant && !collapsed && (
            <li className="pt-3 pb-1">
              <span className="px-3 text-xs font-semibold text-base-content/40 uppercase tracking-wider">
                {t('nav.operations')}
              </span>
            </li>
          )}
          {!isAccountant && collapsed && <li className="border-t border-base-200 my-2" />}
          {!isAccountant && [
            navItem('/maintenances'),
            navItem('/parcels'),
            // EP-141 T141-003 — Staff Calendar is gone from the nav; its grid
            // now lives inside Timesheet (Finance section, below), which is
            // not visitor-management-gated (EP-141 §9 Q-2).
            ...(showVisitorManagement ? [navItem('/visitor-arrivals')] : []),
            navItem('/parking'),
          ].map((item) => (
            <li key={item.href}>
              <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t(item.labelKey) : undefined}>
                <NavLink
                  to={item.href}
                  end={item.end}
                  onClick={handleNavClick}
                  aria-label={t(item.labelKey)}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      computeActive(item.href, isActive)
                        ? 'bg-primary text-primary-content'
                        : 'hover:bg-base-200 text-base-content'
                    } ${collapsed ? 'justify-center' : ''}`
                  }
                >
                  {item.icon}
                  {!collapsed && (
                    <>
                      <span className="flex-1">{t(item.labelKey)}</span>
                      {item.badge && (
                        <Badge variant="count" size="sm">{item.badge}</Badge>
                      )}
                    </>
                  )}
                </NavLink>
              </div>
            </li>
          ))}

          {/* Timesheet — visible to all roles including accountant (own-entry per D-5/Q-4).
              Previously inside the operations array; extracted here so the accountant
              section header suppression above does not hide it. */}
          {(() => {
            const item = navItem('/timesheet')
            return (
              <li key={item.href}>
                <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t(item.labelKey) : undefined}>
                  <NavLink
                    to={item.href}
                    end={item.end}
                    onClick={handleNavClick}
                    aria-label={t(item.labelKey)}
                    data-testid={item.testId}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-primary text-primary-content'
                          : 'hover:bg-base-200 text-base-content'
                      } ${collapsed ? 'justify-center' : ''}`
                    }
                  >
                    {item.icon}
                    {!collapsed && <span className="flex-1">{t(item.labelKey)}</span>}
                  </NavLink>
                </div>
              </li>
            )
          })()}

          {/* Announcements (EP-26, dual-gate) — hidden for accountant (not in D-5 money set) */}
          {showAnnouncements && !isAccountant && (
            <li key="/announcements">
              <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t('nav.announcements') : undefined}>
                <NavLink
                  to="/announcements"
                  onClick={handleNavClick}
                  aria-label={t('nav.announcements')}
                  data-testid="nav-announcements"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-content'
                        : 'hover:bg-base-200 text-base-content'
                    } ${collapsed ? 'justify-center' : ''}`
                  }
                >
                  <Megaphone className="w-5 h-5" />
                  {!collapsed && <span className="flex-1">{t('nav.announcements')}</span>}
                </NavLink>
              </div>
            </li>
          )}

          {/* Incentives Section (EP-74, dual-gate) — hidden for accountant (not in D-5 money set) */}
          {/* Owner and admin only: every incentives policy (campaigns, catalog,
              promotions) is admin_or_higher, so staff got a 403 and the error
              page from each of these links (browser pass, 2026-10-17). */}
          {showIncentivesAny && isAdmin && (
            <>
              {!collapsed && (
                <li className="pt-3 pb-1">
                  <span className="px-3 text-xs font-semibold text-base-content/40 uppercase tracking-wider">
                    {t('nav.incentives')}
                  </span>
                </li>
              )}
              {collapsed && <li className="border-t border-base-200 my-2" />}

              {showIncentivesRewards && (
                <>
                  <li key="/incentives/campaigns">
                    <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t('nav.incentives_campaigns') : undefined}>
                      <NavLink
                        to="/incentives/campaigns"
                        onClick={handleNavClick}
                        aria-label={t('nav.incentives_campaigns')}
                        data-testid="nav-incentives-campaigns"
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                            isActive ? 'bg-primary text-primary-content' : 'hover:bg-base-200 text-base-content'
                          } ${collapsed ? 'justify-center' : ''}`
                        }
                      >
                        <Gift className="w-5 h-5" />
                        {!collapsed && <span className="flex-1">{t('nav.incentives_campaigns')}</span>}
                      </NavLink>
                    </div>
                  </li>
                  <li key="/incentives/catalog">
                    <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t('nav.incentives_catalog') : undefined}>
                      <NavLink
                        to="/incentives/catalog"
                        onClick={handleNavClick}
                        aria-label={t('nav.incentives_catalog')}
                        data-testid="nav-incentives-catalog"
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                            isActive ? 'bg-primary text-primary-content' : 'hover:bg-base-200 text-base-content'
                          } ${collapsed ? 'justify-center' : ''}`
                        }
                      >
                        <Tag className="w-5 h-5" />
                        {!collapsed && <span className="flex-1">{t('nav.incentives_catalog')}</span>}
                      </NavLink>
                    </div>
                  </li>
                </>
              )}

              {/* Promotions belongs to the RETENTION module (renewal_benefits), so it
                  gates on showIncentivesRetention — NOT showIncentivesAny.
                  It was `showIncentivesAny` until 2026-08-05, which meant enabling
                  the REWARDS module leaked the retention nav item: with
                  loyalty_points_enabled=true and renewal_benefits_enabled unset, the
                  item rendered and led to a page whose API 403s "Module not enabled"
                  (IncentiveModuleGates re-checks server-side). Only the section
                  HEADER at :506 legitimately uses showIncentivesAny — that should
                  appear when either module is on. */}
              {showIncentivesRetention && (
                <li key="/incentives/promotions">
                  <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t('nav.incentives_promotions') : undefined}>
                    <NavLink
                      to="/incentives/promotions"
                      onClick={handleNavClick}
                      aria-label={t('nav.incentives_promotions')}
                      data-testid="nav-incentives-promotions"
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                          isActive ? 'bg-primary text-primary-content' : 'hover:bg-base-200 text-base-content'
                        } ${collapsed ? 'justify-center' : ''}`
                      }
                    >
                      <Ticket className="w-5 h-5" />
                      {!collapsed && <span className="flex-1">{t('nav.incentives_promotions')}</span>}
                    </NavLink>
                  </div>
                </li>
              )}
            </>
          )}

          {/* CMMS Section — hidden for accountant; Vendors surfaced separately above */}
          {showCmms && !isAccountant && (
            <>
              {!collapsed && (
                <li className="pt-3 pb-1">
                  <span className="px-3 text-xs font-semibold text-base-content/40 uppercase tracking-wider">
                    {t('nav.cmms')}
                  </span>
                </li>
              )}
              {collapsed && <li className="border-t border-base-200 my-2" />}
              {cmmsNavItems
                // /vendors is MoneyRoute (staff-blocked), so listing it for
                // staff produced a nav link that bounced straight back to /.
                .filter((item) => item.href !== '/vendors' || isAdmin || isAccountant)
                .map((item) => {
                const badge = item.href === '/equipment' && warrantyAlertCount > 0
                  ? warrantyAlertCount : undefined
                return (
                  <li key={item.href}>
                    <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t(item.labelKey) : undefined}>
                      <NavLink
                        to={item.href}
                        end={item.end}
                        onClick={handleNavClick}
                        aria-label={t(item.labelKey)}
                        data-testid={item.testId}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                            isActive
                              ? 'bg-primary text-primary-content'
                              : 'hover:bg-base-200 text-base-content'
                          } ${collapsed ? 'justify-center' : ''}`
                        }
                      >
                        {item.icon}
                        {!collapsed && (
                          <>
                            <span className="flex-1">{t(item.labelKey)}</span>
                            {badge && (
                              <Badge variant="warning" size="sm">{badge}</Badge>
                            )}
                          </>
                        )}
                      </NavLink>
                    </div>
                  </li>
                )
              })}
            </>
          )}

          {/* Bank Reconciliation + Closing Package moved UP into the Finance
              group above — this block used to repeat the Finance header for
              them. It now only wraps the Accounting section. */}
          {(isAdmin || isAccountant) && (
            <>
              {/* Accounting section — visible to admin/owner AND accountant (D-5), feature-flagged */}
              {showAccounting && (
                <>
                  {!collapsed && (
                    <li className="pt-3 pb-1">
                      <span className="px-3 text-xs font-semibold text-base-content/40 uppercase tracking-wider">
                        {t('nav.accounting')}
                      </span>
                    </li>
                  )}
                  {collapsed && <li className="border-t border-base-200 my-2" />}
                  {accountingNavItems.map((item) => (
                    <li key={item.href}>
                      <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t(item.labelKey) : undefined}>
                        <NavLink
                          to={item.href}
                          end={item.end}
                          onClick={handleNavClick}
                          aria-label={t(item.labelKey)}
                          data-testid={item.testId}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                              isActive
                                ? 'bg-primary text-primary-content'
                                : 'hover:bg-base-200 text-base-content'
                            } ${collapsed ? 'justify-center' : ''}`
                          }
                        >
                          {item.icon}
                          {!collapsed && <span className="flex-1">{t(item.labelKey)}</span>}
                        </NavLink>
                      </div>
                    </li>
                  ))}
                </>
              )}

              {/* Debt Collection — admin/owner only, not accountant */}
              {isAdmin && showDebtCollection && (
                <li key="/debt-collection">
                  <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t('nav.debt_collection') : undefined}>
                    <NavLink
                      to="/debt-collection"
                      onClick={handleNavClick}
                      aria-label={t('nav.debt_collection')}
                      data-testid="nav-debt-collection"
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary text-primary-content'
                            : 'hover:bg-base-200 text-base-content'
                        } ${collapsed ? 'justify-center' : ''}`
                      }
                    >
                      <Scale className="w-5 h-5" />
                      {!collapsed && <span className="flex-1">{t('nav.debt_collection')}</span>}
                    </NavLink>
                  </div>
                </li>
              )}
            </>
          )}

          {/* Vendors — visible to admin/owner AND accountant (D-5), regardless of CMMS flag.
              For admin/owner it is also rendered inside the CMMS section above (gated on
              featureFlags.cmms). For accountant the CMMS section is hidden; this standalone
              item ensures it is always reachable from the sidebar. */}
          {isAccountant && (
            <li key="/vendors-accountant">
              <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t('nav.vendors') : undefined}>
                <NavLink
                  to="/vendors"
                  onClick={handleNavClick}
                  aria-label={t('nav.vendors')}
                  data-testid="nav-vendors"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-content'
                        : 'hover:bg-base-200 text-base-content'
                    } ${collapsed ? 'justify-center' : ''}`
                  }
                >
                  <Building className="w-5 h-5" />
                  {!collapsed && <span className="flex-1">{t('nav.vendors')}</span>}
                </NavLink>
              </div>
            </li>
          )}

          {/* Reports — visible to admin/owner AND accountant (D-5).
              Accountant gets Reports access; Team/Templates/ActivityLog remain admin-only. */}
          {(isAdmin || isAccountant) && (
            <li key="/reports-nav">
              <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t('nav.reports') : undefined}>
                <NavLink
                  to="/reports"
                  onClick={handleNavClick}
                  aria-label={t('nav.reports')}
                  data-testid="nav-reports"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-content'
                        : 'hover:bg-base-200 text-base-content'
                    } ${collapsed ? 'justify-center' : ''}`
                  }
                >
                  <BarChart3 className="w-5 h-5" />
                  {!collapsed && <span className="flex-1">{t('nav.reports')}</span>}
                </NavLink>
              </div>
            </li>
          )}

          {/* Admin-only section: Templates, Team, ActivityLog — accountant excluded */}
          {isAdmin && (
            <>
              {!collapsed && (
                <li className="pt-3 pb-1">
                  <span className="px-3 text-xs font-semibold text-base-content/40 uppercase tracking-wider">
                    {t('nav.admin')}
                  </span>
                </li>
              )}
              {collapsed && <li className="border-t border-base-200 my-2" />}
              {/* adminNavItems[0] = Reports — now rendered above for isAdmin || isAccountant;
                  skip it here to avoid a duplicate nav-reports element for admin/owner.
                  Remaining items: Templates, Team, ActivityLog (indices 1, 2, 3). */}
              {adminNavItems
                .slice(1)
                .filter((item) => !supportSession || !SUPPORT_SESSION_HIDDEN_NAV.includes(item.href))
                .map((item) => (
                <li key={item.href}>
                  <div className={collapsed ? 'tooltip tooltip-right' : ''} data-tip={collapsed ? t(item.labelKey) : undefined}>
                    <NavLink
                      to={item.href}
                      end={item.end}
                      onClick={handleNavClick}
                      aria-label={t(item.labelKey)}
                      data-testid={item.testId}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                          isActive
                            ? 'bg-primary text-primary-content'
                            : 'hover:bg-base-200 text-base-content'
                        } ${collapsed ? 'justify-center' : ''}`
                      }
                    >
                      {item.icon}
                      {!collapsed && (
                        <>
                          <span className="flex-1">{t(item.labelKey)}</span>
                          {item.badge && (
                            <Badge variant="count" size="sm">{item.badge}</Badge>
                          )}
                        </>
                      )}
                    </NavLink>
                  </div>
                </li>
              ))}
            </>
          )}
        </ul>
      </nav>

      {/* Settings footer — admin-only (matches route gating), rendered here for
          prominent always-visible access rather than buried in the admin section.
          Logout moved to the Header avatar dropdown (P1) — no longer duplicated here. */}
      {isAdmin && (
        <div className="border-t border-base-300 py-2 px-2">
          <NavLink
            to="/settings"
            end
            data-testid="nav-settings"
            onClick={handleNavClick}
            aria-label={t('nav.settings')}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                isActive ? 'bg-primary text-primary-content' : 'hover:bg-base-200 text-base-content'
              } ${collapsed ? 'justify-center' : ''}`
            }
          >
            <Settings className="w-5 h-5" />
            {!collapsed && <span>{t('nav.settings')}</span>}
          </NavLink>
        </div>
      )}

      {/* User Info & Collapse Toggle */}
      <div className="border-t border-base-300 p-3">
        {!collapsed && user && (
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-medium text-primary">
                {/* F9 (EP-158): the payload has no `name` field (UserSerializer
                    never emits one, only first_name/last_name/full_name), so
                    this always fell through to the email initial. Match the
                    dashboard greeting's own field (full_name over the raw
                    email). */}
                {(user.full_name || user.email).charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user.full_name || user.email}</p>
              <p className="text-xs text-base-content/60 truncate">{user.email}</p>
            </div>
          </div>
        )}
        {onToggle && (
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg hover:bg-base-200 transition-colors text-base-content/60"
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm">{t('actions.collapse')}</span>
              </>
            )}
          </button>
        )}
      </div>
    </aside>
  )
}
